/**
 * Resident API Service
 * Handles all API calls for Resident (Patient) Management
 */

import apiClient from './apiClient';
import { encrypt } from '../utils/encryptionUtils';
import {
  ResidentDTO,
  CreateResidentRequest,
  UpdateResidentRequest,
  PageResponse,
  ResidentDocumentDTO,
  InsuranceCard
} from '../types/resident.types';

const BASE_PATH = '/v1/residents';

/**
 * Resident Service
 * Provides type-safe API calls for resident management
 */
class ResidentService {
  /**
   * Create a new resident (admit patient)
   */
  async createResident(request: CreateResidentRequest, facilityId: string): Promise<ResidentDTO> {
    const payload = { ...request } as any;
    
    // Encrypt sensitive fields
    if (payload.medicalRecordNumber) {
      payload.medicalRecordNumber = encrypt(payload.medicalRecordNumber);
    }
    if (payload.ssn) {
      payload.ssn = encrypt(payload.ssn);
    }

    if (payload.insuranceCards && Array.isArray(payload.insuranceCards)) {
      payload.insuranceCards = payload.insuranceCards.map((card: any) => {
        const newCard = { ...card };
        if (newCard.subscriberRelationship !== undefined) {
          newCard.SubscriberRelationship = newCard.subscriberRelationship;
          delete newCard.subscriberRelationship;
        }
        if (newCard.coverageType !== undefined) {
          newCard.CoverageType = newCard.coverageType;
          delete newCard.coverageType;
        }
        return newCard;
      });
    }

    const response = await apiClient.post<ResidentDTO>(
      `${BASE_PATH}?facilityId=${facilityId}`,
      payload
    );
    return response.data;
  }


  /**
   * Get resident by ID
   */
  async getResidentById(id: string): Promise<ResidentDTO> {
    const response = await apiClient.get<ResidentDTO>(`${BASE_PATH}/${id}`);

    if (response.data && response.data.insuranceCards && Array.isArray(response.data.insuranceCards)) {
      response.data.insuranceCards = response.data.insuranceCards.map((card: any) => {
        const newCard = { ...card };
        if (newCard.SubscriberRelationship !== undefined) {
          newCard.subscriberRelationship = newCard.SubscriberRelationship;
        }
        if (newCard.CoverageType !== undefined) {
          newCard.coverageType = newCard.CoverageType;
        }
        return newCard;
      });
    }

    return response.data;
  }

  /**
   * Get all residents (paginated)
   */
  async getAllResidents(
    page: number = 0,
    size: number = 15,
    facilityId?: string | null,
    status?: string
  ): Promise<PageResponse<ResidentDTO>> {

    const params: any = { page, size };

    // Only include facilityId if user selected it
    if (facilityId) {
      params.facilityId = facilityId;
    }

    if (status && status !== "All") {
      params.status = status;
    }

    const response = await apiClient.get<PageResponse<ResidentDTO>>(BASE_PATH, {
      params
    });

    return response.data;
  }


  /**
   * Search residents by name
   */
  async searchResidents(
    query: string,
    page: number = 0,
    size: number = 20
  ): Promise<PageResponse<ResidentDTO>> {
    const response = await apiClient.get<PageResponse<ResidentDTO>>(
      `${BASE_PATH}/search`,
      {
        params: { q: query, page, size },
      }
    );
    return response.data;
  }

  async quickSearch(
    facilityId: string,
    search: string,
    status?: string,
    signal?: AbortSignal
  ): Promise<ResidentDTO[]> {
    const params: any = { facilityId };

    if (search && search.trim() !== "") {
      params.search = search.trim();
    }

    if (status && status !== "All") {
      params.status = status;
    }

    const response = await apiClient.get<ResidentDTO[]>(
      `${BASE_PATH}/quick-search`,
      { params, signal }
    );
    return response.data;
  }

  /**
   * Update resident information
   */
  async updateResident(
    id: string,
    request: Partial<ResidentDTO>
  ): Promise<ResidentDTO> {
    const payload = { ...request } as any;

    if (payload.insuranceCards && Array.isArray(payload.insuranceCards)) {
      payload.insuranceCards = payload.insuranceCards.map((card: any) => {
        const newCard = { ...card };
        if (newCard.subscriberRelationship !== undefined) {
          newCard.SubscriberRelationship = newCard.subscriberRelationship;
          delete newCard.subscriberRelationship;
        }
        if (newCard.coverageType !== undefined) {
          newCard.CoverageType = newCard.coverageType;
          delete newCard.coverageType;
        }
        return newCard;
      });
    }

    const response = await apiClient.patch<ResidentDTO>(
      `${BASE_PATH}/${id}`,
      payload
    );
    return response.data;
  }

  /**
   * Update resident status
   */
  async updateResidentStatus(id: string, status: string, reason?: string): Promise<void> {
    const payload: any = { status };
    if (reason) {
      payload.reason = reason;
    }
    await apiClient.patch(`${BASE_PATH}/${id}/status`, payload);
  }

  /**
   * Toggle resident status
   */
  async toggleResidentStatus(id: string): Promise<ResidentDTO> {
    const response = await apiClient.patch<ResidentDTO>(
      `${BASE_PATH}/${id}/toggle-status`
    );
    return response.data;
  }

  /**
   * Discharge resident from facility
   */
  async dischargeResident(
    id: string,
    reason: string
  ): Promise<ResidentDTO> {
    const response = await apiClient.post<ResidentDTO>(
      `${BASE_PATH}/${id}/discharge`,
      null,
      {
        params: { reason },
      }
    );
    return response.data;
  }

  /**
   * Get resident insurances
   */
  async getResidentInsurances(residentId: string): Promise<InsuranceCard[]> {
    const response = await apiClient.get<InsuranceCard[]>(
      `${BASE_PATH}/${residentId}/insurances`
    );

    if (response.data && Array.isArray(response.data)) {
      return response.data.map((card: any) => {
        const newCard = { ...card };
        if (newCard.SubscriberRelationship !== undefined) {
          newCard.subscriberRelationship = newCard.SubscriberRelationship;
        }
        if (newCard.CoverageType !== undefined) {
          newCard.coverageType = newCard.CoverageType;
        }
        return newCard;
      });
    }

    return response.data;
  }

  /**
   * Add insurance to resident
   */
  async addInsurance(residentId: string, insurance: InsuranceCard): Promise<InsuranceCard> {
    const payload = { ...insurance } as any;
    if (payload.subscriberRelationship !== undefined) {
      payload.SubscriberRelationship = payload.subscriberRelationship;
      delete payload.subscriberRelationship;
    }
    if (payload.coverageType !== undefined) {
      payload.CoverageType = payload.coverageType;
      delete payload.coverageType;
    }

    const response = await apiClient.post<InsuranceCard>(
      `${BASE_PATH}/${residentId}/insurances`,
      payload
    );
    return response.data;
  }

  /**
   * Update resident insurance
   */
  async updateInsurance(residentId: string, insuranceId: string, insurance: InsuranceCard): Promise<InsuranceCard> {
    const payload = { ...insurance } as any;
    if (payload.subscriberRelationship !== undefined) {
      payload.SubscriberRelationship = payload.subscriberRelationship;
      delete payload.subscriberRelationship;
    }
    if (payload.coverageType !== undefined) {
      payload.CoverageType = payload.coverageType;
      delete payload.coverageType;
    }

    const response = await apiClient.put<InsuranceCard>(
      `/v1/insurances/${insuranceId}`,
      payload
    );
    return response.data;
  }

  /**
   * Delete resident insurance
   */
  async deleteInsurance(residentId: string, insuranceId: string): Promise<void> {
    await apiClient.delete(`/v1/insurances/${insuranceId}`);
  }

  /**
   * Delete resident (soft delete/archive)
   */
  async deleteResident(id: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${id}`);
  }
  // residentService.ts
  async getResidentDocuments(residentId: string): Promise<ResidentDocumentDTO[]> {
    const response = await apiClient.get<ResidentDocumentDTO[]>(
      `/v1/residents/${residentId}/doc`
    );
    return response.data;
  }

  /**
   * Upload document for resident
   */
  async uploadDocument(residentId: string, formData: FormData): Promise<void> {
    await apiClient.post(`/v1/residents/${residentId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  /**
   * Upload resident profile image
   */
  async uploadProfileImage(residentId: string, file: File): Promise<string> {
    const formData = new FormData();
    formData.append('profilePicture', file);
    const response = await apiClient.put(`${BASE_PATH}/${residentId}/profile-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  /**
   * Remove resident profile image
   */
  async deleteProfileImage(residentId: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${residentId}/profile-image/delete`);
  }

  /**
   * Get resident profile picture as a Blob
   */
  async getProfilePicture(residentId: string): Promise<Blob> {
    const response = await apiClient.get<ArrayBuffer>(
      `${BASE_PATH}/${residentId}/profile-picture`,
      {
        responseType: 'arraybuffer'
      }
    );
    const mimeType = response.headers['content-type'] || 'image/jpeg';
    return new Blob([response.data], { type: mimeType });
  }

  /**
   * Upload resident photo (deprecated/alternative)
   */
  async uploadResidentPhoto(residentId: string, file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${BASE_PATH}/${residentId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data; // Assuming it returns the URL or the updated resident
  }

  /**
   * Get residents by active facility (paginated)
   */
  async getResidentsByFacility(
    facilityId: string,
    page: number = 0,
    size: number = 15
  ): Promise<PageResponse<ResidentDTO>> {
    const response = await apiClient.get<PageResponse<ResidentDTO>>(
      `${BASE_PATH}/facilities/${facilityId}`,
      {
        params: { page, size }
      }
    );
    return response.data;
  }

  /**
   * Get resident PDF
   */
  async getResidentPdf(id: string, signal?: AbortSignal): Promise<Blob> {
    const response = await apiClient.get<ArrayBuffer>(
      `${BASE_PATH}/residents/${id}/pdf`,
      {
        responseType: 'arraybuffer',
        signal
      }
    );
    const mimeType = response.headers['content-type'] || 'application/pdf';
    return new Blob([response.data], { type: mimeType });
  }

  /**
   * Get clinic document PDF
   */
  async getClinicDocumentPdf(id: string, signal?: AbortSignal): Promise<Blob> {
    const response = await apiClient.get<ArrayBuffer>(
      `${BASE_PATH}/${id}/clinic-document/pdf`,
      {
        responseType: 'arraybuffer',
        signal
      }
    );
    const mimeType = response.headers['content-type'] || 'application/pdf';
    return new Blob([response.data], { type: mimeType });
  }

  /**
   * Get resident PDF with selected sections
   */
  async getResidentPdfWithSections(id: string, sections: string[], signal?: AbortSignal): Promise<Blob> {
    const params = new URLSearchParams();
    sections.forEach(section => params.append('sections', section));
    const response = await apiClient.get<ArrayBuffer>(
      `${BASE_PATH}/residents/${id}/pdf-with-sections?${params.toString()}`,
      {
        responseType: 'arraybuffer',
        signal
      }
    );
    const mimeType = response.headers['content-type'] || 'application/pdf';
    return new Blob([response.data], { type: mimeType });
  }

  /**
   * Get discharge summary PDF
   */
  async getDischargeSummaryPdf(id: string, signal?: AbortSignal): Promise<Blob> {
    const response = await apiClient.get<ArrayBuffer>(
      `${BASE_PATH}/${id}/discharge-summary/pdf`,
      {
        responseType: 'arraybuffer',
        signal
      }
    );
    const mimeType = response.headers['content-type'] || 'application/pdf';
    return new Blob([response.data], { type: mimeType });
  }

  /**
   * Get active resident count for a facility
   */
  async getActiveResidentCount(facilityId: string): Promise<number> {
    const response = await apiClient.get<number>(
      `${BASE_PATH}/facilities/${facilityId}/count`
    );
    return response.data;
  }

  /**
   * Get all resident status counts for the dashboard in a single call.
   * Response shape: { totalResidents: N, residentStatusCounts: { Active: N, Discharged: N, ... } }
   */
  async getDashboardCounts(facilityId: string): Promise<any> {
    const response = await apiClient.get<any>(
      `${BASE_PATH}/facilities/${facilityId}/dashboard-counts`
    );
    const raw = response.data;
    if (!raw) return {};
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      // Return the full payload (which contains totalResidents, activeMedicationOrders, and residentStatusCounts)
      if (raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) return raw.data;
      if (raw.result && typeof raw.result === 'object' && !Array.isArray(raw.result)) return raw.result;
      return raw;
    }
    return {};
  }

  /**
   * Get recent admissions
   */
  async getRecentAdmissions(facilityId: string): Promise<ResidentDTO[]> {
    const response = await apiClient.get<ResidentDTO[]>(
      `${BASE_PATH}/facilities/${facilityId}/recent-admissions`
    );
    return response.data;
  }

  /**
   * Get residents by status
   */
  async getResidentsByStatus(facilityId: string, status: string): Promise<ResidentDTO[]> {
    const response = await apiClient.get<ResidentDTO[]>(
      `${BASE_PATH}/facilities/${facilityId}/status/${status}`
    );
    return response.data;
  }

  /**
   * Get residents by admission type (e.g. ALF, Hospice)
   */
  async getResidentsByAdmissionType(facilityId: string, type: string): Promise<ResidentDTO[]> {
    const response = await apiClient.get<ResidentDTO[]>(
      `${BASE_PATH}/${facilityId}/admission-type/${type}`
    );
    return response.data;
  }

  // ========== Helper Methods ==========

  /**
   * Calculate age from date of birth
   */
  calculateAge(dateOfBirth: string): number {
    const birth = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    return age;
  }

  /**
   * Format full name
   */
  getFullName(resident: ResidentDTO): string {
    const { firstName, middleName, lastName } = resident;
    return middleName
      ? `${firstName} ${middleName} ${lastName}`
      : `${firstName} ${lastName}`;
  }
  async downloadDocument(documentId: string): Promise<Blob> {
    const response = await apiClient.get<ArrayBuffer>(
      `${BASE_PATH}/documents/${documentId}/download`,
      {
        responseType: 'arraybuffer'
      }
    );

    const mimeType =
      response.headers['content-type'] || 'application/octet-stream';

    return new Blob([response.data], { type: mimeType });
  }

  async deleteDocument(residentId: string, documentId: string): Promise<void> {
    await apiClient.delete(`/v1/residents/documents/${documentId}`);
  }




  async openDocument(documentId: string, inline: boolean = true, fileName?: string): Promise<void> {
    const blob = await this.downloadDocument(documentId);
    const url = window.URL.createObjectURL(blob);

    if (inline) {
      // Open in new tab (browser decides whether to preview or download)
      window.open(url, '_blank');
    } else {
      // Force download
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }

    window.URL.revokeObjectURL(url);
  }


  /**
   * Get room assignment display
   */
  getRoomAssignment(resident: ResidentDTO): string {
    const { roomNumber, bedNumber } = resident;
    if (!roomNumber) return 'Not Assigned';
    return bedNumber ? `Room ${roomNumber}, Bed ${bedNumber}` : `Room ${roomNumber}`;
  }

  /**
   * Check if resident is active
   */
  isActive(resident: ResidentDTO): boolean {
    return resident.status === 'ACTIVE' && resident.isActive !== false;
  }

  /**
   * Get active resident lookup for a facility
   */
  async getActiveLookup(facilityId: string): Promise<ResidentDTO[]> {
    const response = await apiClient.get<any[]>(
      `${BASE_PATH}/facilities/${facilityId}/active-lookup`
    );
    return (response.data || []).map(r => ({
      ...r,
      id: r.id || r.residentId,
      status: r.status || 'ACTIVE'
    }));
  }


  /**
   * Get status display color
   */
  getStatusColor(status: string): 'success' | 'error' | 'warning' | 'info' | 'default' {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'DISCHARGED':
        return 'info';
      case 'DECEASED':
        return 'error';
      case 'TRANSFERRED':
        return 'warning';
      case 'OUT_OF_NURSING_HOME':
        return 'warning';
      case 'HOSPITAL_APPOINTMENT':
        return 'info';
      default:
        return 'default';
    }
  }

  /**
   * Get care level display color
   */
  getCareLevelColor(careLevel: string): 'error' | 'warning' | 'info' | 'success' {
    switch (careLevel) {
      case 'CRITICAL':
        return 'error';
      case 'HIGH':
        return 'warning';
      case 'MEDIUM':
        return 'info';
      case 'LOW':
        return 'success';
      default:
        return 'info';
    }
  }
}

// Export singleton instance
export default new ResidentService();